import type { JSX, KeyboardEvent, ReactNode } from 'react'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Меню действий («три точки»).
 *
 * Прошлая версия рисовала список внутри карточки обычным абсолютным блоком, и
 * он вылезал за её край, налезая на соседнюю карточку — прямая жалоба со
 * скриншотом. Причина в том, что у карточек есть `overflow: hidden` и свой
 * контекст наложения: абсолютный список внутри них обречён.
 *
 * Здесь список рендерится ПОРТАЛОМ в `body` с `position: fixed` и координатами,
 * посчитанными от кнопки. Дальше он сам выбирает сторону:
 *  - не хватает места снизу → раскрывается вверх;
 *  - не хватает справа → выравнивается по правому краю кнопки;
 *  - в любом случае не выходит за вьюпорт (зажим по 8px от кромок).
 *
 * Клавиатура: Enter/Space/↓ открывают, ↑↓ двигают, Enter выбирает, Esc
 * закрывает и возвращает фокус, Tab закрывает. Пункты — `role="menuitem"`.
 */

export interface MenuItem {
  label: string
  onSelect: () => void
  icon?: ReactNode
  disabled?: boolean
  /** Разрушающее действие: цвет + отделено сверху линией. */
  destructive?: boolean
  /** Подсказка на `title` — например, «нет прав». */
  title?: string
}

export interface MenuProps {
  label: string
  items: MenuItem[]
  /** Своя кнопка вместо «трёх точек» (например, аватар пользователя). */
  trigger?: ReactNode
  className?: string
}

const GAP = 6
const EDGE = 8
const ITEM_H = 34

export function Menu({ label, items, trigger, className }: MenuProps): JSX.Element {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [box, setBox] = useState<{ top: number; left: number; minWidth: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const place = useCallback((): void => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect === undefined) return
    const height = items.length * ITEM_H + 10
    const width = Math.max(rect.width, 208)

    const openUp = rect.bottom + GAP + height > window.innerHeight && rect.top > height + GAP
    const top = openUp ? rect.top - GAP - height : rect.bottom + GAP
    // Прижимаем к правому краю кнопки, потом зажимаем во вьюпорт.
    const rawLeft = rect.right - width
    const left = Math.min(Math.max(EDGE, rawLeft), window.innerWidth - width - EDGE)

    setBox({
      top: Math.min(Math.max(EDGE, top), window.innerHeight - height - EDGE),
      left,
      minWidth: width
    })
  }, [items.length])

  // Позицию считаем в момент открытия: пока меню закрыто, координаты триггера
  // никого не интересуют, а на каждый ререндер их мерить — лишние layout-чтения.
  useLayoutEffect(() => {
    if (!open) return
    place()
    setActive(items.findIndex(item => item.disabled !== true))
  }, [open, items, place])

  useEffect(() => {
    if (open) listRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = (): void => setOpen(false)
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) === true) return
      if (listRef.current?.contains(target) === true) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    // Скролл и ресайз закрывают: пересчитывать позицию на каждый кадр скролла
    // дороже, чем закрыть меню, которое пользователь всё равно потерял из вида.
    document.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const step = (delta: number): void => {
    setActive(previous => {
      let next = previous
      for (let guard = 0; guard < items.length; guard++) {
        next = (next + delta + items.length) % items.length
        if (items[next]?.disabled !== true) return next
      }
      return previous
    })
  }

  const commit = (index: number): void => {
    const item = items[index]
    if (item === undefined || item.disabled === true) return
    setOpen(false)
    triggerRef.current?.focus()
    item.onSelect()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      step(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      step(-1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      commit(active)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    } else if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(previous => !previous)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          'press inline-flex shrink-0 items-center justify-center rounded-sm text-ink-muted',
          trigger === undefined && 'size-9 hover:bg-panel-2 hover:text-ink',
          className
        )}
      >
        {trigger ?? <MoreHorizontal aria-hidden className="size-4.5" />}
      </button>

      {open && box !== null
        ? createPortal(
            <div
              ref={listRef}
              id={id}
              role="menu"
              aria-label={label}
              tabIndex={-1}
              onKeyDown={onKeyDown}
              style={{ top: box.top, left: box.left, minWidth: box.minWidth }}
              className="anim-pop fixed z-popover rounded-md border border-line-strong bg-panel-2/95 p-1 shadow-lg backdrop-blur-xl"
            >
              {items.map((item, index) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  title={item.title}
                  onPointerEnter={() => item.disabled !== true && setActive(index)}
                  onClick={() => commit(index)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-xs px-2.5 py-2 text-left text-base',
                    'disabled:cursor-not-allowed disabled:opacity-45',
                    item.destructive === true ? 'text-fail' : 'text-ink-muted',
                    index === active && item.disabled !== true && 'bg-panel-3 text-ink',
                    index === active && item.destructive === true && 'bg-fail/12 text-fail',
                    // Разрушающее действие отделяем от обычных, чтобы «удалить»
                    // не оказалось соседом «перезапустить» без границы.
                    item.destructive === true && index > 0 && 'mt-1 border-t border-line pt-2'
                  )}
                >
                  {item.icon === undefined ? null : <span className="shrink-0">{item.icon}</span>}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  )
}
